import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { CalendarDays, ExternalLink, RefreshCw, Building2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_COLOR = {
  BASIS: "#4A5D4E",
  "MEEST GEKOZEN": "#B37A5F",
  "VAAK GEKOZEN": "#B37A5F",
  PROFESSIONEEL: "#6B705C",
  GEAVANCEERD: "#5C635A",
  GEAVANCEERRD: "#5C635A",
  ONDERNEMING: "#9C6A53",
  SPIRITUEEL: "#8A6B9C",
};

const colorForCategory = (c) => CATEGORY_COLOR[c?.toUpperCase()] || "#5C635A";

const formatFetched = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

function EventCard({ ev, idx }) {
  const color = colorForCategory(ev.category);
  return (
    <article
      className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8"
      data-testid={`agenda-event-${idx}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <span
            className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] text-white"
            style={{ backgroundColor: color }}
            data-testid={`agenda-event-category-${idx}`}
          >
            {ev.category}
          </span>
          <div className="flex items-center gap-2 text-[#5C635A] text-sm">
            <CalendarDays strokeWidth={1.5} size={16} />
            <span data-testid={`agenda-event-dates-${idx}`}>{ev.dates}</span>
          </div>
        </div>
        {ev.link && (
          <a
            href={ev.link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`agenda-event-link-${idx}`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4A5D4E] hover:underline shrink-0"
          >
            Meer informatie
            <ExternalLink strokeWidth={1.5} size={14} />
          </a>
        )}
      </div>
      <h3
        className="font-heading text-2xl sm:text-3xl text-[#1A1D1A] tracking-tight font-semibold mb-3"
        data-testid={`agenda-event-title-${idx}`}
      >
        {ev.title}
      </h3>
      <p
        className="text-sm sm:text-base text-[#5C635A] leading-relaxed whitespace-pre-line"
        data-testid={`agenda-event-description-${idx}`}
      >
        {ev.description}
      </p>
    </article>
  );
}

function GroupBlock({ group, idx, canRefresh, onRefresh, refreshing }) {
  return (
    <section
      className="bg-[#F9F8F6] border border-[#E5E2DC] rounded-2xl p-6 sm:p-8"
      data-testid={`agenda-group-${idx}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center border border-[#E5E2DC]">
            <Building2 strokeWidth={1.5} className="text-[#B37A5F]" size={18} />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8A9188]">
              Institute
            </p>
            <h3
              className="font-heading text-xl text-[#1A1D1A] tracking-tight font-semibold"
              data-testid={`agenda-group-name-${idx}`}
            >
              {group.institute_name}
            </h3>
            {group.events_url && (
              <a
                href={group.events_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#5C635A] hover:underline break-all"
                data-testid={`agenda-group-url-${idx}`}
              >
                {group.events_url}
              </a>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {canRefresh && group.events_url && (
            <Button
              onClick={onRefresh}
              disabled={refreshing}
              variant="outline"
              data-testid={`agenda-group-refresh-${idx}`}
              className="h-9 rounded-lg border-[#E5E2DC] text-[#1A1D1A] hover:bg-[#F2F0EA]"
            >
              <RefreshCw strokeWidth={1.5} size={14} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
          )}
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A9188] font-bold">
            {group.error
              ? group.error
              : group.fetched_at
              ? `Updated ${formatFetched(group.fetched_at)}`
              : "Never updated"}
          </p>
        </div>
      </div>

      {group.events.length === 0 ? (
        <p className="text-sm text-[#5C635A] italic">
          {group.events_url ? "No events found at the configured URL yet." : "No events URL configured for this institute yet."}
        </p>
      ) : (
        <div className="space-y-4">
          {group.events.map((ev, i) => (
            <EventCard key={`${ev.title}-${i}`} ev={ev} idx={`${idx}-${i}`} />
          ))}
        </div>
      )}
    </section>
  );
}

export default function UpcomingEvents() {
  const { user } = useAuth();
  const { t } = useT();
  const isSuperAdmin = user?.role === "super_admin";
  const canRefresh = user?.role === "super_admin" || user?.role === "institute_admin";

  const [singlePayload, setSinglePayload] = useState(null); // for non-super_admin
  const [groups, setGroups] = useState([]); // for super_admin
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingGroupId, setRefreshingGroupId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agenda/events");
      if (data.groups) {
        setGroups(data.groups);
      } else {
        setSinglePayload(data);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load events");
    } finally {
      setLoading(false);
    }
  };

  const refreshSelf = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/agenda/events/refresh");
      setSinglePayload(data);
      if (data.error) toast.error(data.error);
      else toast.success(`Loaded ${data.events?.length || 0} events`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const refreshGroup = async (instituteId) => {
    setRefreshingGroupId(instituteId);
    try {
      const { data } = await api.post(`/institutes/${instituteId}/events/refresh`);
      setGroups((prev) => prev.map((g) => (g.institute_id === instituteId ? data : g)));
      if (data.error) toast.error(data.error);
      else toast.success(`Loaded ${data.events?.length || 0} events`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Refresh failed");
    } finally {
      setRefreshingGroupId(null);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="px-8 lg:px-12 py-12 max-w-5xl" data-testid="upcoming-events-page">
      <PageHeader
        overline={isSuperAdmin ? "Across all institutes" : "Your institute"}
        title={t("events.title")}
        description={t("events.description")}
      />

      {!isSuperAdmin && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 mb-6"
          data-testid="agenda-meta"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-[#8A9188] font-bold">
            {loading
              ? "Loading…"
              : !singlePayload?.events_url
              ? "No events URL has been configured for your institute yet"
              : singlePayload?.stale
              ? `Showing cached data · could not refresh · last updated ${formatFetched(singlePayload.fetched_at)}`
              : singlePayload?.fetched_at
              ? `Last updated ${formatFetched(singlePayload.fetched_at)}`
              : "No events fetched yet"}
          </p>
          {canRefresh && singlePayload?.events_url && (
            <Button
              onClick={refreshSelf}
              disabled={refreshing || loading}
              data-testid="agenda-refresh-button"
              variant="outline"
              className="h-9 rounded-lg border-[#E5E2DC] text-[#1A1D1A] hover:bg-[#F2F0EA]"
            >
              <RefreshCw
                strokeWidth={1.5}
                size={16}
                className={`mr-2 ${refreshing ? "animate-spin" : ""}`}
              />
              {refreshing ? `${t("events.refresh")}…` : t("events.refresh")}
            </Button>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-[#8A9188] uppercase tracking-[0.2em] font-bold">
          {t("events.loading")}
        </p>
      ) : isSuperAdmin ? (
        groups.length === 0 ? (
          <div
            className="bg-[#F2F0EA] border border-[#E5E2DC] rounded-2xl p-10 text-center text-[#5C635A]"
            data-testid="agenda-empty"
          >
            No institutes yet.
          </div>
        ) : (
          <div className="space-y-6" data-testid="agenda-groups">
            {groups.map((g, i) => (
              <GroupBlock
                key={g.institute_id}
                group={g}
                idx={i}
                canRefresh={canRefresh}
                onRefresh={() => refreshGroup(g.institute_id)}
                refreshing={refreshingGroupId === g.institute_id}
              />
            ))}
          </div>
        )
      ) : !singlePayload?.events?.length ? (
        <div
          className="bg-[#F2F0EA] border border-[#E5E2DC] rounded-2xl p-10 text-center text-[#5C635A]"
          data-testid="agenda-empty"
        >
          {singlePayload?.error ||
            (singlePayload?.events_url
              ? "No upcoming events found yet."
              : "Your institute admin hasn't configured an events URL yet.")}
        </div>
      ) : (
        <div className="space-y-5" data-testid="agenda-list">
          {singlePayload.events.map((ev, idx) => (
            <EventCard key={`${ev.title}-${idx}`} ev={ev} idx={idx} />
          ))}
        </div>
      )}
    </div>
  );
}
